import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { INavData } from '@coreui/angular';
import { global_user } from '../../../global/global';
import { TokenStorageService } from '../../../login/token-storage.service';
import { BusinessLogoService } from '../../../services/business-logo.service';

export interface MobileNavActionItem {
  label: string;
  icon?: string;
  url?: string | any[];
  action?: string;
  danger?: boolean;
}

export interface MobileNavLanguageItem {
  code: string;
  label: string;
  icon?: any;
}

@Component({
    selector: 'app-mobile-nav',
    templateUrl: './mobile-nav.component.html',
    styleUrls: ['./mobile-nav.component.scss'],
    standalone: false
})
export class MobileNavComponent implements OnChanges, OnDestroy {
  @Input() items: INavData[] = [];
  @Input() accountItems: MobileNavActionItem[] = [];
  @Input() languageItems: MobileNavLanguageItem[] = [];
  @Input() currentLanguage = 'en';
  @Input() notificationCount = 0;
  @Input() notificationsRoute: string | any[] = '/pages/notifications';
  @Input() visible = false;
  @Input() hiddenUrls: string[] = [];
  @Output() close = new EventEmitter<void>();
  @Output() actionRequested = new EventEmitter<string>();
  @Output() languageSelected = new EventEmitter<string>();

  /** The business's uploaded logo, fetched the first time the menu opens. */
  logoUrl: string | null = null;
  private logoRequested = false;

  /** Set once the list scrolls, folding the heading and shrinking the business card into a slim bar. */
  compact = false;

  constructor(
    private router: Router,
    private tokenStorage: TokenStorageService,
    private logos: BusinessLogoService
  ) {}

  /** Who is signed in: the login e-mail, else the business account's own e-mail. */
  get accountEmail(): string {
    return this.tokenStorage.accountEmail() || global_user.user.email || '';
  }

  get companyName(): string {
    return global_user.user.company_name || '';
  }

  get accountInitial(): string {
    return (this.companyName || this.accountEmail || '?').charAt(0).toLocaleUpperCase('tr');
  }

  ngOnChanges(changes: SimpleChanges): void {
    // The page underneath must not scroll while the sheet is open, and each opening starts with the full heading.
    if (changes['visible']) {
      document.body.style.overflow = this.visible ? 'hidden' : '';
      if (!this.visible) this.compact = false;
    }
    if (!this.visible || this.logoRequested || this.tokenStorage.isPickerSession()) return;
    this.logoRequested = true;
    this.logos.get().subscribe({
      next: (logo) => (this.logoUrl = this.logos.imageUrl(logo?.path)),
      error: () => (this.logoUrl = null),
    });
  }

  ngOnDestroy(): void {
    document.body.style.overflow = '';
  }

  onBodyScroll(event: Event): void {
    const compact = (event.target as HTMLElement).scrollTop > 16;
    if (compact !== this.compact) this.compact = compact;
  }

  get actionableItems(): INavData[] {
    return (this.items || [])
      .filter((item) => !item.title && item.url)
      .filter((item) => !this.shouldHideItem(item));
  }

  async navigateTo(url?: string | any[]) {
    if (!url) {
      return;
    }
    if (Array.isArray(url)) {
      await this.router.navigate(url);
    } else {
      await this.router.navigateByUrl(url);
    }
    this.close.emit();
  }

  onAccountItemClicked(item: MobileNavActionItem): void {
    if (item.url) {
      this.navigateTo(item.url);
      return;
    }

    if (item.action) {
      this.actionRequested.emit(item.action);
      this.close.emit();
    }
  }

  selectLanguage(code: string): void {
    this.languageSelected.emit(code);
    this.close.emit();
  }

  private shouldHideItem(item: INavData): boolean {
    if (!this.hiddenUrls?.length) {
      return false;
    }
    const url = item.url;
    if (!url) {
      return false;
    }
    if (Array.isArray(url)) {
      return url.some((segment) => typeof segment === 'string' && this.hiddenUrls.includes(segment));
    }
    return typeof url === 'string' && this.hiddenUrls.includes(url);
  }
}
